import { Button, Checkbox, Group, Loader, Stack, Text } from '@mantine/core';
import { useState } from 'react';

import { Chart } from '@e-origin/shared';

import { useDeclarants } from '@admin/hooks';
import { MultiSelect, NumberInput, TextInput } from '@components';
import { useEditChartFormInitializer } from './useEditChartFormInitializer';

interface EditChartProps {
  chart?: Chart;
  onHide: () => void;
}

export const EditChart: React.FC<EditChartProps> = ({ chart, onHide }) => {
  const [formError, setFormError] = useState<string | null>(null);
  const { data: declarantsData } = useDeclarants();

  const { form, onSubmit, isSubmitting } = useEditChartFormInitializer({
    chart,
  });

  const handleSubmit = async (values) => {
    setFormError(null);

    try {
      await onSubmit(values);
      onHide();
    } catch (error: any) {
      setFormError(error?.response?.data?.message || 'Failed to save chart');
    }
  };

  const declarantsSelectData =
    declarantsData?.map((declarant) => ({
      value: declarant._id,
      label: declarant.accountName,
    })) || [];

  const selectedDeclarantValues = (form.values.declarants || []).map((id: string) => {
    const found = declarantsSelectData.find((item) => item.value === id);
    return found ? found.label : id;
  });

  const handleDeclarantsChange = (selectedIds: string[]) => {
    form.setFieldValue('declarants', selectedIds);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md" my="xl">
        <TextInput {...form.getInputProps('name')} label="Chart Name" placeholder="Enter chart name" />
        <NumberInput {...form.getInputProps('order')} label="Order" placeholder="Order" />

        <Group>
          <Checkbox {...form.getInputProps('forSupervisorOnly', { type: 'checkbox' })} label="For Supervisor Only" checked={form.values.forSupervisorOnly} />
          <Checkbox {...form.getInputProps('isChart', { type: 'checkbox' })} label="Is Chart" checked={form.values.isChart} />
        </Group>

        <MultiSelect
          label="Declarants"
          data={declarantsSelectData}
          value={selectedDeclarantValues}
          onChange={handleDeclarantsChange}
          fetchOptions={async () => declarantsSelectData}
        />

        <TextInput {...form.getInputProps('chartId')} label="Chart ID" placeholder="Chart ID" />
      </Stack>

      {formError && (
        <Text c="red" fw="bold" mt="md">
          {formError}
        </Text>
      )}

      <Group justify="center" mt="xl">
        <Button type="button" key="cancel-modal-btn" onClick={() => onHide()} disabled={isSubmitting} size="lg" variant="transparent">
          Cancel
        </Button>

        <Button
          key="save-modal-btn"
          disabled={!form.isValid() || isSubmitting}
          type="submit"
          leftSection={isSubmitting && <Loader color="white" />}
          variant="filled"
          size="lg"
        >
          Save
        </Button>
      </Group>
    </form>
  );
};
